import { useState, useRef, useCallback } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  Package,
  Upload,
  Download,
  FileText,
  Loader2,
  CheckCircle2,
  Sparkles,
  X,
  Files,
  Zap,
  TrendingUp,
  Layers,
} from "lucide-react";
import { Progress } from "@/components/ui/progress";

const MAX_FILES = 5;

// Top seller codes to highlight
const TOP_SELLER_CODES = ["8000", "1021", "M57", "ST10", "1076", "570"];
const ML_FORMATS = ["30ml", "50ml", "100ml", "200ml", "500ml"];

// Futuristic color palette for top sellers - dark backgrounds with visible text
const CODE_COLORS = {
  "8000": { bg: "from-slate-800 to-slate-900", text: "text-cyan-400", border: "border-cyan-500/60", glow: "shadow-cyan-500/20", accent: "bg-cyan-500" },
  "1021": { bg: "from-slate-800 to-slate-900", text: "text-violet-400", border: "border-violet-500/60", glow: "shadow-violet-500/20", accent: "bg-violet-500" },
  "M57": { bg: "from-slate-800 to-slate-900", text: "text-emerald-400", border: "border-emerald-500/60", glow: "shadow-emerald-500/20", accent: "bg-emerald-500" },
  "ST10": { bg: "from-slate-800 to-slate-900", text: "text-amber-400", border: "border-amber-500/60", glow: "shadow-amber-500/20", accent: "bg-amber-500" },
  "1076": { bg: "from-slate-800 to-slate-900", text: "text-rose-400", border: "border-rose-500/60", glow: "shadow-rose-500/20", accent: "bg-rose-500" },
  "570": { bg: "from-slate-800 to-slate-900", text: "text-sky-400", border: "border-sky-500/60", glow: "shadow-sky-500/20", accent: "bg-sky-500" },
};

// Animated stat card component
const StatCard = ({ label, value, icon: Icon, delay = 0 }) => (
  <div 
    className="relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-900/80 to-gray-800/50 border border-gray-700/50 p-4 backdrop-blur-sm animate-slideUp"
    style={{ animationDelay: `${delay}ms` }}
  >
    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full animate-shimmer" />
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg bg-gradient-to-br from-[#C9A227]/20 to-[#C9A227]/5">
        <Icon className="w-5 h-5 text-[#C9A227]" />
      </div>
      <div>
        <p className="text-2xl font-bold text-white">{value}</p>
        <p className="text-xs text-gray-400">{label}</p>
      </div>
    </div>
  </div>
);

// Top seller card with futuristic design
const TopSellerCard = ({ code, data, delay = 0 }) => {
  const colors = CODE_COLORS[code] || CODE_COLORS["8000"];
  const hasData = data.total > 0;
  
  return (
    <div 
      className={`relative overflow-hidden rounded-2xl border-2 ${colors.border} bg-gradient-to-br ${colors.bg} p-5 transition-all duration-300 hover:scale-[1.02] hover:shadow-xl ${colors.glow} animate-slideUp`}
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Accent bar on top */}
      <div className={`absolute top-0 left-0 right-0 h-1 ${colors.accent}`} />
      
      {/* Code badge */}
      <div className="flex items-center justify-between mb-4">
        <div className={`px-4 py-2 rounded-lg ${colors.accent}/20 border ${colors.border}`}>
          <span className={`font-mono font-bold text-xl ${colors.text}`}>{code}</span>
        </div>
        {hasData && (
          <div className={`flex items-center gap-1 px-3 py-1.5 rounded-lg bg-black/40 ${colors.text}`}>
            <TrendingUp className="w-4 h-4" />
            <span className="text-lg font-bold">{data.total}</span>
          </div>
        )}
      </div>
      
      {/* Format breakdown */}
      <div className="grid grid-cols-5 gap-3 mt-4">
        {ML_FORMATS.map((format) => {
          const qty = data[format] || 0;
          return (
            <div key={format} className="text-center p-2 rounded-lg bg-black/20">
              <p className="text-xs text-gray-400 mb-1 font-medium">{format.replace('ml', '')}</p>
              <p className={`font-bold text-xl ${qty > 0 ? 'text-white' : 'text-gray-600'}`}>
                {qty || "-"}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// File list component
const FileListItem = ({ file, index, onRemove, disabled }) => (
  <div
    className="flex items-center justify-between p-3 bg-gray-800/50 rounded-xl border border-gray-700/50 backdrop-blur-sm animate-slideIn"
    style={{ animationDelay: `${index * 50}ms` }}
  >
    <div className="flex items-center gap-3">
      <div className="p-2 rounded-lg bg-gradient-to-br from-[#C9A227]/20 to-[#C9A227]/5">
        <FileText className="w-5 h-5 text-[#C9A227]" />
      </div>
      <div>
        <p className="text-sm font-medium text-white truncate max-w-[200px]">{file.name}</p>
        <p className="text-xs text-gray-500">
          {(file.size / 1024 / 1024).toFixed(2)} MB
        </p>
      </div>
    </div>
    {!disabled && (
      <button
        onClick={() => onRemove(index)}
        className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors group"
      >
        <X className="w-4 h-4 text-gray-500 group-hover:text-red-400 transition-colors" />
      </button>
    )}
  </div>
);

const PickingList = () => {
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [progress, setProgress] = useState(0);
  const fileRef = useRef(null);

  // Drag handlers
  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files) {
      addFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFileSelect = useCallback((e) => {
    if (e.target.files) {
      addFiles(Array.from(e.target.files));
    }
  }, []);

  const addFiles = useCallback((newFiles) => {
    const pdfFiles = newFiles.filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length !== newFiles.length) {
      toast.error("Solo file PDF sono accettati");
    }
    setFiles(prev => {
      const totalFiles = [...prev, ...pdfFiles].slice(0, MAX_FILES);
      if (totalFiles.length < prev.length + pdfFiles.length) {
        toast.warning(`Massimo ${MAX_FILES} file consentiti`);
      }
      return totalFiles;
    });
  }, []);

  const removeFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Process files
  const processFiles = async () => {
    if (files.length === 0) {
      toast.error("Seleziona almeno un file PDF");
      return;
    }

    setProcessing(true);
    setResult(null);
    setProgress(10);

    try {
      const formData = new FormData();
      for (let i = 0; i < files.length; i++) {
        formData.append("files", files[i]);
      }

      // Simulate progress
      const progressInterval = setInterval(() => {
        setProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const res = await api.post("/picking/merge", formData, {
        timeout: 180000, // 3 minutes for large files
      });

      clearInterval(progressInterval);
      setProgress(100);

      if (res.data && res.data.success) {
        setResult(res.data);
        toast.success(res.data.message);
        setFiles([]);
      } else {
        toast.error(res.data?.detail || "Errore nell'elaborazione");
      }
    } catch (error) {
      console.error("Picking list merge error:", error);
      const errorMessage = error.response?.data?.detail || 
                          error.message || 
                          "Errore nell'elaborazione dei PDF";
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
      setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  // Download merged PDF
  const downloadPDF = useCallback(() => {
    if (!result?.pdf) return;
    
    const byteCharacters = atob(result.pdf.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: "application/pdf" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = result.pdf.filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    toast.success(`Download: ${result.pdf.filename}`);
  }, [result]);

  return (
    <div data-testid="picking-list-page" className="space-y-6 animate-fadeIn">
      {/* Custom CSS for animations */}
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes shimmer {
          to { transform: translateX(100%); }
        }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0; transform: translateX(-100%); }
          50% { opacity: 1; transform: translateX(100%); }
        }
        @keyframes glow {
          0%, 100% { box-shadow: 0 0 20px rgba(201, 162, 39, 0.3); }
          50% { box-shadow: 0 0 40px rgba(201, 162, 39, 0.5); }
        }
        .animate-slideUp { animation: slideUp 0.5s ease-out forwards; }
        .animate-slideIn { animation: slideIn 0.3s ease-out forwards; }
        .animate-shimmer { animation: shimmer 2s infinite; }
        .animate-pulse-slow { animation: pulse-slow 3s infinite; }
        .animate-glow { animation: glow 2s infinite; }
      `}</style>

      {/* Header with futuristic design */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-gray-700/50 p-6">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAwIDEwIEwgNDAgMTAgTSAxMCAwIEwgMTAgNDAgTSAwIDIwIEwgNDAgMjAgTSAyMCAwIEwgMjAgNDAgTSAwIDMwIEwgNDAgMzAgTSAzMCAwIEwgMzAgNDAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjAzKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-50" />
        <div className="relative">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-3 rounded-xl bg-gradient-to-br from-[#C9A227]/30 to-[#C9A227]/10 border border-[#C9A227]/30 animate-glow">
              <Zap className="w-7 h-7 text-[#C9A227]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-white via-gray-200 to-gray-400 bg-clip-text text-transparent">
                Picking List
              </h1>
              <p className="text-gray-400 text-sm">
                Unisci più picking list in una sola • Max {MAX_FILES} file
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Area */}
      <Card className="bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 border-gray-700/50 shadow-xl backdrop-blur-sm overflow-hidden">
        <CardContent className="p-6">
          {/* Drag & Drop Zone */}
          <div
            className={`relative border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 ${
              dragActive
                ? "border-[#C9A227] bg-[#C9A227]/10 scale-[1.02]"
                : "border-gray-600 hover:border-[#C9A227]/50 hover:bg-gray-800/50"
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".pdf"
              multiple
              onChange={handleFileSelect}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              disabled={processing || files.length >= MAX_FILES}
            />
            
            {processing ? (
              <div className="space-y-4">
                <div className="relative">
                  <Loader2 className="w-16 h-16 text-[#C9A227] mx-auto animate-spin" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-[#C9A227] animate-pulse" />
                  </div>
                </div>
                <div>
                  <p className="text-white font-medium text-lg">Elaborazione in corso...</p>
                  <p className="text-sm text-gray-400">Analisi di {files.length} file PDF</p>
                </div>
                <Progress value={progress} className="w-64 mx-auto h-2" />
              </div>
            ) : (
              <div className="space-y-4">
                <div className="w-20 h-20 bg-gradient-to-br from-[#C9A227]/30 to-[#C9A227]/10 rounded-2xl flex items-center justify-center mx-auto border border-[#C9A227]/30 transition-transform hover:scale-110">
                  <Files className="w-10 h-10 text-[#C9A227]" />
                </div>
                <div>
                  <p className="text-white font-medium text-lg">
                    Trascina qui i PDF oppure clicca
                  </p>
                  <p className="text-sm text-gray-400 mt-1">
                    {files.length > 0 
                      ? `${files.length}/${MAX_FILES} file selezionati`
                      : `Max ${MAX_FILES} file PDF • Fino a 200 ordini per file`}
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-6 space-y-3">
              {files.map((file, index) => (
                <FileListItem
                  key={`${file.name}-${index}`}
                  file={file}
                  index={index}
                  onRemove={removeFile}
                  disabled={processing}
                />
              ))}
            </div>
          )}

          {/* Process Button */}
          {files.length > 0 && !processing && (
            <Button
              onClick={processFiles}
              className="w-full mt-6 bg-gradient-to-r from-[#C9A227] to-[#b8922a] text-white hover:from-[#b8922a] hover:to-[#a7821f] h-14 text-lg font-semibold rounded-xl transition-all hover:scale-[1.02] hover:shadow-lg hover:shadow-[#C9A227]/20"
              data-testid="picking-process-btn"
            >
              <Zap className="w-5 h-5 mr-2" />
              Unifica {files.length} Picking List
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Results Section */}
      {result && (
        <div className="space-y-6 animate-fadeIn">
          {/* Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              label="File Elaborati" 
              value={result.files_processed} 
              icon={Files} 
              delay={0} 
            />
            <StatCard 
              label="Pagine Totali" 
              value={result.total_pages} 
              icon={FileText} 
              delay={100} 
            />
            <StatCard 
              label="Prodotti Unici" 
              value={result.total_products} 
              icon={Package} 
              delay={200} 
            />
            <StatCard 
              label="Quantità Totale" 
              value={result.total_quantity} 
              icon={Layers} 
              delay={300} 
            />
          </div>

          {/* Download Button */}
          <Card className="bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 border-gray-700/50 shadow-xl overflow-hidden">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-xl bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30">
                    <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-white font-semibold text-lg">Picking List Unificata</p>
                    <p className="text-gray-400 text-sm">
                      {result.total_products} prodotti • {result.total_quantity} pezzi totali • <span data-testid="picking-total-liters">{result.total_liters ?? 0} L</span>
                    </p>
                  </div>
                </div>
                <Button
                  onClick={downloadPDF}
                  className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white hover:from-emerald-600 hover:to-emerald-700 h-12 px-6 rounded-xl font-semibold transition-all hover:scale-105 hover:shadow-lg hover:shadow-emerald-500/20"
                  data-testid="picking-download-btn"
                >
                  <Download className="w-5 h-5 mr-2" />
                  Scarica PDF
                </Button>
              </div>
            </CardContent>
          </Card>

          {result.unknown_items?.length > 0 && (
            <div className="rounded-xl border border-red-300 bg-red-50 p-4" data-testid="picking-unknown-items">
              <p className="text-red-700 font-bold mb-2">
                {result.unknown_quantity} pezzi non riconosciuti, esclusi dal conteggio: controllali a mano
              </p>
              <ul className="space-y-1 text-sm text-red-900">
                {result.unknown_items.map((u) => (
                  <li key={u.label} className="flex justify-between gap-3">
                    <span className="truncate">{u.label}</span><span className="font-mono font-bold">x{u.quantity}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Top Sellers Grid */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#C9A227]" />
              <h2 className="text-xl font-bold text-gray-900">Codici Top Seller</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TOP_SELLER_CODES.map((code, index) => (
                <TopSellerCard
                  key={code}
                  code={code}
                  data={result.top_sellers[code] || { "30ml": 0, "50ml": 0, "100ml": 0, "200ml": 0, "500ml": 0, total: 0 }}
                  delay={index * 100}
                />
              ))}
            </div>
          </div>

          {/* All Products Table */}
          {result.all_products && Object.keys(result.all_products).length > 0 && (
            <Card className="bg-gradient-to-br from-gray-900/95 via-gray-800/95 to-gray-900/95 border-gray-700/50 shadow-xl overflow-hidden">
              <CardHeader className="border-b border-gray-700/50">
                <CardTitle className="text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-[#C9A227]" />
                  Tutti i Prodotti ({Object.keys(result.all_products).length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-gray-800/95 backdrop-blur-sm">
                      <tr className="text-left">
                        <th className="p-4 text-gray-400 font-medium text-sm">Codice</th>
                        <th className="p-4 text-gray-400 font-medium text-sm text-center">30ml</th>
                        <th className="p-4 text-gray-400 font-medium text-sm text-center">50ml</th>
                        <th className="p-4 text-gray-400 font-medium text-sm text-center">100ml</th>
                        <th className="p-4 text-gray-400 font-medium text-sm text-center">200ml</th>
                        <th className="p-4 text-gray-400 font-medium text-sm text-center">500ml</th>
                        <th className="p-4 text-gray-400 font-medium text-sm text-center">Totale</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(result.all_products)
                        .sort((a, b) => {
                          const totalA = Object.values(a[1]).reduce((sum, v) => sum + v, 0);
                          const totalB = Object.values(b[1]).reduce((sum, v) => sum + v, 0);
                          return totalB - totalA;
                        })
                        .map(([code, formats], index) => {
                          const total = Object.values(formats).reduce((sum, v) => sum + v, 0);
                          const isTopSeller = TOP_SELLER_CODES.includes(code);
                          return (
                            <tr 
                              key={code} 
                              className={`border-t border-gray-700/30 hover:bg-gray-800/50 transition-colors ${isTopSeller ? 'bg-[#C9A227]/5' : ''}`}
                            >
                              <td className="p-4">
                                <span className={`font-mono font-semibold ${isTopSeller ? 'text-[#C9A227]' : 'text-white'}`}>
                                  {code}
                                </span>
                              </td>
                              <td className="p-4 text-center text-gray-300">{formats["30ml"] || "-"}</td>
                              <td className="p-4 text-center text-gray-300">{formats["50ml"] || "-"}</td>
                              <td className="p-4 text-center text-gray-300">{formats["100ml"] || "-"}</td>
                              <td className="p-4 text-center text-gray-300">{formats["200ml"] || "-"}</td>
                              <td className="p-4 text-center text-gray-300">{formats["500ml"] || "-"}</td>
                              <td className="p-4 text-center font-bold text-white">{total}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
};

export default PickingList;
